import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { skipToken } from '@tanstack/react-query';
import { captureException } from 'r/utils';
import { fetchApiRaw, GET_EVENTS_PLAY } from 'r/utils/fetchApiRaw';
import type { SanseiEvent } from 'r/types';
import {
  buildFilterString,
  serializeSort,
  type FilterCondition,
} from './utils';
import { pairsAtom } from './pairs';

export const eventsManagerFiltersAtom = atom({
  searchQuery: '',
  showDeletedOnly: false,
  currentPage: 1,
  pageSize: 12,
});

export const eventsManagerQueryAtom = atomWithQuery((get) => {
  const filters = get(eventsManagerFiltersAtom);
  const pairs = get(pairsAtom);

  return {
    queryKey: [
      'cms-events-play',
      filters.searchQuery,
      filters.currentPage,
      filters.pageSize,
      filters.showDeletedOnly,
    ],
    queryFn:
      !pairs ? skipToken : (
        async () => {
          try {
            const filterConditions: Record<string, FilterCondition> = {};

            if (filters.searchQuery.trim()) {
              const searchPattern = `%${filters.searchQuery.trim()}%`;
              filterConditions.$or = [
                { tweetText: { $ilike: searchPattern } },
                { kolUsername: { $ilike: searchPattern } },
                { title: { $ilike: searchPattern } },
              ];
            }

            if (filters.showDeletedOnly) {
              filterConditions.deletedAt = { $ne: null };
            } else {
              filterConditions.deletedAt = null;
            }

            const queryParams: Record<string, string> = {
              skip: String((filters.currentPage - 1) * filters.pageSize),
              take: String(filters.pageSize),
              include: 'eventOutcomes',
              paranoid: filters.showDeletedOnly ? 'false' : 'true',
            };

            if (Object.keys(filterConditions).length > 0) {
              queryParams.filter = encodeURIComponent(
                buildFilterString(filterConditions),
              );
            }

            queryParams.sort = serializeSort('createdAt', 'DESC');

            const response = await fetchApiRaw(GET_EVENTS_PLAY, {
              queryParams,
            });

            return {
              data:
                Array.isArray(response.data) ?
                  (response.data as SanseiEvent[])
                : [],
              total: response.total || 0,
            };
          } catch (e) {
            captureException(e);
            return { data: [], total: 0 };
          }
        }
      ),
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  };
});
